import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Send } from 'lucide-react';

const FREE_EMAIL_DOMAINS = [
  'hotmail.com', 'hotmail.com.br', 'hotmail.es',
  'outlook.com', 'outlook.com.br', 'outlook.es',
  'yahoo.com', 'yahoo.com.br', 'yahoo.es', 'ymail.com',
  'icloud.com', 'me.com', 'mac.com',
  'live.com', 'live.com.br', 'msn.com',
  'aol.com',
  'protonmail.com', 'proton.me', 'pm.me',
  'bol.com.br', 'terra.com.br', 'uol.com.br', 'ig.com.br',
  'globomail.com', 'globo.com',
  'zoho.com', 'mail.com', 'gmx.com', 'gmx.net',
  'yandex.com', 'yandex.ru', 'tutanota.com',
  'rocketmail.com', 'inbox.com'
];

const maskPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const fieldLabel = "block text-sm font-semibold text-gray-700 mb-1.5";
const fieldBase = "w-full px-4 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 transition-all focus:outline-none focus:ring-2 focus:ring-[#eb068c]/30";
const fieldOk = "border-gray-200 focus:border-[#eb068c]";
const fieldErr = "border-red-400 focus:border-red-500 focus:ring-red-200";

export const LeadFormModal = ({ open, onOpenChange }) => {
  const initial = { name: '', email: '', whatsapp: '', company: '', eventDate: '', quantity: '', message: '' };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 3) e.name = 'Informe seu nome completo';
    if (!form.email.trim()) e.email = 'Informe seu e-mail corporativo';
    else {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailTrim = form.email.trim().toLowerCase();
      if (!re.test(emailTrim)) e.email = 'E-mail inválido';
      else {
        const [localPart, domain] = emailTrim.split('@');
        const blockedWords = ['teste', 'testando', 'tester'];
        if (blockedWords.some((w) => localPart.includes(w))) {
          e.email = 'Informe um e-mail real (e-mails de teste não são aceitos)';
        } else if (FREE_EMAIL_DOMAINS.includes(domain)) {
          e.email = 'Use um e-mail corporativo ou Gmail (outros e-mails gratuitos não são aceitos)';
        }
      }
    }
    const digits = form.whatsapp.replace(/\D/g, '');
    if (digits.length < 10) e.whatsapp = 'Informe um WhatsApp válido com DDD';
    if (!form.company.trim()) e.company = 'Informe o nome da empresa';
    if (!form.eventDate) e.eventDate = 'Informe a data do evento ou ação';
    if (!form.quantity || Number(form.quantity) < 20) e.quantity = 'Quantidade mínima de 20 unidades';
    if (form.message.trim() && form.message.trim().length < 160) e.message = 'A mensagem deve ter pelo menos 160 caracteres';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        access_key: process.env.REACT_APP_WEB3FORMS_KEY,
        subject: `Novo lead B2B - ${form.company}`,
        from_name: 'Plantinha de Geladeira - Site B2B',
        botcheck: '',
        'Nome Completo': form.name,
        'E-mail Corporativo': form.email,
        'WhatsApp': form.whatsapp,
        'Empresa': form.company,
        'Data do Evento/Ação': form.eventDate,
        'Quantidade Estimada': form.quantity,
        'Mensagem': form.message || '(não preenchido)'
      };
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-11077389987/cPCkCKfg36AcEKOdjqIp' });
        }
        onOpenChange(false);
        setForm(initial);
        window.location.href = '/obrigado';
      } else {
        alert('Não foi possível enviar agora. Tente novamente em instantes.');
      }
    } catch (err) {
      alert('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
        <div className="bg-gradient-to-r from-[#eb068c] to-[#c7056f] px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl md:text-3xl font-bold">
              Solicite seu Orçamento
            </DialogTitle>
            <DialogDescription className="text-pink-50 text-sm md:text-base mt-1">
              Preencha os campos abaixo e nossa equipe entra em contato em até 24h 🌱
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="lead-form" noValidate>
          {/* Honeypot */}
          <input type="checkbox" name="botcheck" className="hidden" tabIndex="-1" autoComplete="off" />

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Nome */}
            <div>
              <label className={fieldLabel}>Nome Completo <span className="text-[#eb068c]">*</span></label>
              <input
                type="text"
                data-testid="form-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Ex: Maria da Silva"
                className={`${fieldBase} ${errors.name ? fieldErr : fieldOk}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>

            {/* E-mail */}
            <div>
              <label className={fieldLabel}>E-mail <span className="text-[#eb068c]">*</span></label>
              <input
                type="email"
                data-testid="form-email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="voce@suaempresa.com.br ou Gmail"
                className={`${fieldBase} ${errors.email ? fieldErr : fieldOk}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            {/* WhatsApp */}
            <div>
              <label className={fieldLabel}>WhatsApp <span className="text-[#eb068c]">*</span></label>
              <input
                type="tel"
                data-testid="form-whatsapp"
                value={form.whatsapp}
                onChange={(e) => set('whatsapp', maskPhone(e.target.value))}
                placeholder="(11) 99999-9999"
                maxLength={16}
                className={`${fieldBase} ${errors.whatsapp ? fieldErr : fieldOk}`}
              />
              {errors.whatsapp && <p className="mt-1 text-xs text-red-600">{errors.whatsapp}</p>}
            </div>

            {/* Empresa */}
            <div>
              <label className={fieldLabel}>Nome da Empresa <span className="text-[#eb068c]">*</span></label>
              <input
                type="text"
                data-testid="form-company"
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
                placeholder="Ex: Empresa Brasil Ltda"
                className={`${fieldBase} ${errors.company ? fieldErr : fieldOk}`}
              />
              {errors.company && <p className="mt-1 text-xs text-red-600">{errors.company}</p>}
            </div>

            {/* Data */}
            <div>
              <label className={fieldLabel}>Data do Evento/Ação <span className="text-[#eb068c]">*</span></label>
              <input
                type="date"
                data-testid="form-event-date"
                value={form.eventDate}
                onChange={(e) => set('eventDate', e.target.value)}
                className={`${fieldBase} ${errors.eventDate ? fieldErr : fieldOk}`}
              />
              {errors.eventDate && <p className="mt-1 text-xs text-red-600">{errors.eventDate}</p>}
            </div>

            {/* Quantidade */}
            <div>
              <label className={fieldLabel}>Quantidade Mínima Estimada <span className="text-[#eb068c]">*</span></label>
              <input
                type="number"
                data-testid="form-quantity"
                min="20"
                value={form.quantity}
                onChange={(e) => set('quantity', e.target.value)}
                placeholder="Mínimo 20 unidades"
                className={`${fieldBase} ${errors.quantity ? fieldErr : fieldOk}`}
              />
              {errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
            </div>
          </div>

          {/* Mensagem - full width */}
          <div>
            <label className={fieldLabel}>
              Deixe sua mensagem <span className="text-gray-400 font-normal">(opcional, mínimo 160 caracteres se preenchido)</span>
            </label>
            <textarea
              data-testid="form-message"
              rows="3"
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              placeholder="Conte um pouco sobre o objetivo da ação, prazo, personalização desejada..."
              className={`${fieldBase} ${errors.message ? fieldErr : fieldOk} resize-none`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.message ? (
                <p className="text-xs text-red-600">{errors.message}</p>
              ) : (
                <span className="text-xs text-gray-400">{form.message.length} caracteres</span>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="form-submit"
            className="w-full bg-[#20BA5A] hover:bg-[#178C41] text-white py-6 text-base font-semibold rounded-xl shadow-lg transition-all disabled:opacity-60"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando...</>
            ) : (
              <><Send className="mr-2 h-5 w-5" /> Enviar solicitação</>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Ao enviar, você concorda em receber retorno da nossa equipe comercial.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
